import { useRef, useLayoutEffect, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const TESTIMONIALS = [
  {
    quote: 'Manvinder listens deeply and asks thoughtful questions that shift your perspective. Her coaching helped me lead with greater presence, empathy, and authenticity.',
    name: 'Monika B.',
    role: 'Business Leader',
    depth: 1,
  },
  {
    quote: 'Manvinder’s calm presence created space to reflect, challenge old beliefs, and move forward with greater clarity.',
    name: 'Amrit D.',
    role: 'Healthcare Professional',
    depth: 2,
  },
  {
    quote: 'Manvinder helped me navigate a major life transition with greater clarity, confidence, and a renewed sense of direction.',
    name: 'Priya M.',
    role: 'Entrepreneur',
    depth: 3,
  },
  {
    quote: 'Manvinder helped me reconnect with myself and feel safe in my own body again. I’m calmer now than I’ve been in years.',
    name: 'Elena R.',
    role: 'Finance Professional',
    depth: 2,
  },
  {
    quote: 'Manvinder helped me rediscover my identity, set boundaries without guilt, and feel empowered to choose my life.',
    name: 'Kavita R.',
    role: 'Technology Professional',
    depth: 1,
  },
];

const POSITIONS = [
  'md:top-[6%] md:left-[4%] md:w-[30%]',
  'md:top-[14%] md:right-[8%] md:w-[26%]',
  'md:top-[44%] md:left-[26%] md:w-[28%]',
  'md:top-[58%] md:right-[4%] md:w-[29%]',
  'md:top-[70%] md:left-[3%] md:w-[26%]',
];

const Testimonials = () => {
  const root = useRef(null);
  const field = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray('.t-card').forEach((card) => {
        const depth = Number(card.dataset.depth);
        gsap.fromTo(card, { opacity: 0, y: 90 }, {
          opacity: 1, y: 0, duration: 2, ease: 'power2.out',
          scrollTrigger: { trigger: card, start: 'top 88%' },
        });
        gsap.to(card, {
          yPercent: -8 * depth, ease: 'none',
          scrollTrigger: { trigger: root.current, start: 'top bottom', end: 'bottom top', scrub: 1.6 },
        });
      });
    }, root);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (window.matchMedia('(max-width: 767px)').matches) return;
    const cards = field.current.querySelectorAll('.t-card-inner');
    const onMove = (e) => {
      const cx = e.clientX / window.innerWidth - 0.5;
      const cy = e.clientY / window.innerHeight - 0.5;
      cards.forEach((c) => {
        const depth = Number(c.parentElement.dataset.depth);
        gsap.to(c, { x: cx * depth * 14, y: cy * depth * 10, duration: 2.4, ease: 'power2.out' });
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <section ref={root} data-testid="testimonials-section" className="relative bg-[#EDE8DD] py-32 md:py-48 overflow-hidden">
      <div className="px-6 md:px-16 mb-20 md:mb-28 relative z-10">
        <p className="text-[11px] uppercase tracking-[0.4em] text-[#B85C38] mb-6">Voices</p>
        <h2 className="font-serif-display font-light text-4xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#2C2C2A]">
          Memories, <em className="text-[#1A4D38]">floating home.</em>
        </h2>
      </div>

      <div ref={field} className="relative px-6 md:px-0 md:h-[130vh] space-y-8 md:space-y-0">
        {TESTIMONIALS.map((t, i) => (
          <div
            key={t.name}
            data-depth={t.depth}
            data-testid={`testimonial-card-${i}`}
            className={`t-card md:absolute w-full ${POSITIONS[i]} will-change-transform`}
          >
            <div
              className="t-card-inner bg-[#FAFAF8]/90 backdrop-blur-sm p-8 md:p-10 shadow-[0_20px_60px_-20px_rgba(44,44,42,0.15)] will-change-transform"
              style={{ animation: `gentleFloat ${8 + t.depth * 2}s ease-in-out ${i * 1.3}s infinite` }}
            >
              <p className="font-serif-display italic font-light text-lg md:text-xl leading-[1.7] text-[#2C2C2A] mb-7">"{t.quote}"</p>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#1A4D38]">{t.name}</p>
              <p className="text-[11px] tracking-[0.1em] text-[#2C2C2A]/50 mt-1">{t.role}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Testimonials;
